import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { isTrustedDevice } from 'utils/mobile-devices';
import LockIcon from '@mui/icons-material/Lock';
import styles from './styles.module.css';

const getStatusColor = (status, isExpired) => {
  if (isExpired) return 'default';
  if (status === 'BLOCKED') return 'error';
  if (status === 'TRUSTED') return 'success';
  return 'default';
};

const getStatusLabel = (status, isExpired, t) =>
  t(`organization.organization-mobile-devices-info.${status.toLowerCase()}`);

const getRowClassName = (status, isExpired) => {
  if (isExpired) return `${styles.tableRow} ${styles.expired}`;
  if (status === 'BLOCKED') return `${styles.tableRow} ${styles.blocked}`;
  if (status === 'TRUSTED') return `${styles.tableRow} ${styles.trusted}`;
  return `${styles.tableRow} ${styles.normal}`;
};

const AdminMobileDeviceTable = ({ devices = [], onRowClick, ...props }) => {
  const { t } = useTranslation();

  const columns = [
    { id: 'device', label: t('organization.organization-mobile-devices-info.text-device'), minWidth: 200 },
    { id: 'organization', label: t('organization.organization-mobile-devices-info.text-organization'), minWidth: 150 },
    { id: 'model', label: t('organization.organization-mobile-devices-info.text-model'), minWidth: 120 },
    { id: 'uid', label: t('organization.organization-mobile-devices-info.text-uid'), minWidth: 120 },
    { id: 'version', label: t('organization.organization-mobile-devices-info.text-app-version'), minWidth: 100 },
    { id: 'status', label: t('organization.organization-mobile-devices-info.text-device-status'), minWidth: 100 },
    { id: 'expiration', label: t('organization.organization-mobile-devices-info.text-expiration'), minWidth: 150 },
    { id: 'synced', label: t('organization.organization-mobile-devices-info.text-pending-syncs'), minWidth: 120 },
    {
      id: 'lastAccess',
      label: t('organization.organization-mobile-devices-info.text-last-access-list'),
      minWidth: 150,
    },
  ];

  const formatDate = (dateString) => (dateString ? new Date(dateString).toLocaleString(i18next.language) : '-');
  const formatDateOnly = (dateString) => (dateString ? new Date(dateString).toLocaleDateString(i18next.language) : '-');

  const getVariant = (status, isExpired) => {
    if (isExpired) return 'NORMAL';
    if (status === 'NORMAL') return 'outlined';
    return 'filled';
  };

  return (
    <TableContainer component={Paper} elevation={0} className={styles.tableContainer} {...props}>
      <Table className={styles.table} aria-label="mobile devices table">
        <TableHead>
          <TableRow className={styles.tableHeaderRow}>
            {columns.map((column) => (
              <TableCell key={column.id} className={styles.tableHeaderCell}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {devices.map((device) => {
            const isExpired = device.expiration ? new Date(device.expiration) < new Date() : false;

            return (
              <TableRow
                key={device.org_device_id}
                onClick={() => onRowClick(device)}
                className={getRowClassName(device.status, isExpired)}
                data-testid={`device-row-${device.org_device_id}`}
              >
                {/* Device Info */}
                <TableCell>
                  <Box className={styles.deviceInfo}>
                    <Box>
                      <Typography variant="body2" className={styles.deviceName}>
                        {device.name}
                      </Typography>
                      {device.status === 'BLOCKED' && <LockIcon className={styles.lockIcon} />}
                    </Box>
                  </Box>
                </TableCell>

                {/* Organization */}
                <TableCell>
                  <Typography variant="body2" className={styles.tableCellText}>
                    {device.organization_name}
                  </Typography>
                </TableCell>

                {/* Model */}
                <TableCell>
                  <Typography variant="body2" className={styles.tableCellText}>
                    {device.model}
                  </Typography>
                </TableCell>

                {/* UID */}
                <TableCell>
                  <Typography variant="body2" className={styles.tableCellText}>
                    {device.org_device_id}
                  </Typography>
                </TableCell>

                {/* App Version */}
                <TableCell>
                  <Typography variant="body2" className={styles.tableCellText}>
                    {device.app_version}
                  </Typography>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Chip
                    label={getStatusLabel(device.status, isExpired, t)}
                    color={getStatusColor(device.status, isExpired)}
                    size="small"
                    variant={getVariant(device.status, isExpired)}
                    className={isExpired ? `${styles.statusChip} ${styles.expiredStatus}` : styles.statusChip}
                    style={isExpired ? { color: 'dark' } : {}}
                  />
                </TableCell>

                {/* Expiration */}
                <TableCell>
                  {isExpired && (
                    <Typography variant="body2" color="error" fontWeight="bold" display="block" mb={0.5}>
                      {t('organization.organization-mobile-devices-info.expired')}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    className={styles.tableCellText}
                    color={isExpired ? 'error' : 'inherit'}
                    fontWeight={isExpired ? 'bold' : 'normal'}
                  >
                    {formatDateOnly(device.expiration)}
                  </Typography>
                </TableCell>

                {/* Synced Members */}
                <TableCell>
                  <Typography variant="body2" className={styles.tableCellText}>
                    {isTrustedDevice(device.status) && device.synced_members
                      ? `${device.synced_members.nb_synced_members} ${t(
                          'organization.organization-mobile-devices-info.text-of',
                        )} ${device.synced_members.nb_members}`
                      : '-'}
                  </Typography>
                </TableCell>

                {/* Last Access */}
                <TableCell>
                  <Typography variant="body2" className={styles.tableCellText}>
                    {formatDate(device.last_access)}
                  </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

AdminMobileDeviceTable.propTypes = {
  devices: PropTypes.array,
  onRowClick: PropTypes.func,
};

export default AdminMobileDeviceTable;
